/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, cssMap, jsx } from '@atlaskit/css';
import Icon from '@atlaskit/icon';
import type { CustomGlyphProps, IconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';

const sizeStyles = cssMap({
	small: { width: '16px', height: '16px' },
	medium: { width: '24px', height: '24px' },
	large: { width: '32px', height: '32px' },
	xlarge: { width: '48px', height: '48px' },
});

const svgStyles = css({
	fill: token('elevation.surface'),
	overflow: 'hidden',
	pointerEvents: 'none',
});

export interface PresenceUnavailableCustomIconProps {
	label: string;
	primaryColor?: string;
	secondaryColor?: string;
	size?: 'small' | 'medium' | 'large' | 'xlarge';
	testId?: string;
	className?: string;
}

const PresenceUnavailableCustomIcon = ({
	label,
	size = 'medium',
	primaryColor = 'currentColor',
	secondaryColor,
	testId,
	className,
}: PresenceUnavailableCustomIconProps) => (
	<svg
		viewBox="0 0 24 24"
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			color: primaryColor,
			fill: secondaryColor,
		}}
		css={[svgStyles, sizeStyles[size]]}
		aria-label={label || undefined}
		role={label ? 'img' : 'presentation'}
		data-testid={testId}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={className}
	>
		<rect width="24" height="24" fill="white" fillOpacity="0.01" />
		<path
			d="M15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12ZM6 12C6 10.4087 6.63214 8.88258 7.75736 7.75736C8.88258 6.63214 10.4087 6 12 6C13.5913 6 15.1174 6.63214 16.2426 7.75736C17.3679 8.88258 18 10.4087 18 12C18 13.5913 17.3679 15.1174 16.2426 16.2426C15.1174 17.3679 13.5913 18 12 18C10.4087 18 8.88258 17.3679 7.75736 16.2426C6.63214 15.1174 6 13.5913 6 12Z"
			fill="#42526E"
		/>
	</svg>
);

const PresenceUnavailableSVG = (props: CustomGlyphProps) => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		data-testid={props['data-testid']}
		aria-label={props['aria-label']}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={props.className}
	>
		<rect width="24" height="24" fill="white" fillOpacity="0.01" />
		<path
			d="M15 12C15 11.2044 14.6839 10.4413 14.1213 9.87868C13.5587 9.31607 12.7956 9 12 9C11.2044 9 10.4413 9.31607 9.87868 9.87868C9.31607 10.4413 9 11.2044 9 12C9 12.7956 9.31607 13.5587 9.87868 14.1213C10.4413 14.6839 11.2044 15 12 15C12.7956 15 13.5587 14.6839 14.1213 14.1213C14.6839 13.5587 15 12.7956 15 12ZM6 12C6 10.4087 6.63214 8.88258 7.75736 7.75736C8.88258 6.63214 10.4087 6 12 6C13.5913 6 15.1174 6.63214 16.2426 7.75736C17.3679 8.88258 18 10.4087 18 12C18 13.5913 17.3679 15.1174 16.2426 16.2426C15.1174 17.3679 13.5913 18 12 18C10.4087 18 8.88258 17.3679 7.75736 16.2426C6.63214 15.1174 6 13.5913 6 12Z"
			fill="#42526E"
		/>
	</svg>
);

const PresenceUnavailableIcon = (props: IconProps): React.JSX.Element => (
	// eslint-disable-next-line @atlaskit/design-system/no-custom-icons
	<Icon {...props} glyph={PresenceUnavailableSVG} />
);

const icon: React.FC<PresenceUnavailableCustomIconProps & IconProps> = componentWithFG(
	'enghealth-custom-icon-migration',
	PresenceUnavailableCustomIcon,
	PresenceUnavailableIcon,
);
export default icon;
